"use client";
import { Swiper } from "swiper/types";
import { ReactNode, useEffect, useMemo, useState } from "react";
import {
  MotionStack,
  MotionStackProps,
  MotionTypography,
  MotionTypographyProps,
} from "@cc/motion-components";
import { StaticImageData } from "next/image";
import AnimatedButton, { AnimatedButtonProps } from "@cc/animated-button";
import { BehaviorSubject } from "rxjs";
import { useObservable } from "react-use";
import { H5_1 } from "@theme/components/typography.fontvariant";
import { AnimatePresence, MotionProps } from "framer-motion";
import SwiperNavigationButton, {
  SwiperNavigationButtonProps,
} from "@cc/swiper-navigation-button";
import { Stack, StackProps } from "@mui/material";
import { CalendarToday, FmdGood } from "@mui/icons-material";

export interface ArrowSlideInfoProps extends Omit<StackProps, "children"> {
  data: Partial<{
    prefix: string;
    suffix: {
      date: string;
      region: string;
    };
    applyDate: string;
    title: string;
    description: string | null;
    image: string | StaticImageData;
    btnText: string | ReactNode;
    btnLink?: string | null;
  }>[];
  SlotProps?: {
    PrefixTypographyProps?: Omit<MotionTypographyProps, "children">;
    SuffixTypographyProps?: Omit<MotionTypographyProps, "children">;
    TitleTypographyProps?: Omit<MotionTypographyProps, "children">;
    DescriptionTypographyProps?: Omit<MotionTypographyProps, "children">;
    ButtonProps?: Omit<AnimatedButtonProps, "children">;
  };
  isNavigation?: boolean;
  InfoWrapperProps?: Omit<MotionStackProps, "children">;
  SwiperInstance: BehaviorSubject<Swiper | null>;
  SwiperNavigationButtonProps?: SwiperNavigationButtonProps;
  initialSlide?: number;
}

const ArrowSlideInfo = (props: ArrowSlideInfoProps) => {
  const {
    data,
    isNavigation,
    InfoWrapperProps,
    SwiperInstance,
    SwiperNavigationButtonProps,
    SlotProps = {},
    initialSlide = 0,
    ...restStackProps
  } = props;
  const {
    PrefixTypographyProps,
    SuffixTypographyProps,
    TitleTypographyProps,
    DescriptionTypographyProps,
    ButtonProps,
  } = SlotProps;
  const swiper = useObservable(SwiperInstance, null);
  const [activeIndex, setActiveIndex] = useState(initialSlide);

  const { prefix, suffix, applyDate, title, description, btnText, btnLink } =
    useMemo(() => {
      return data[activeIndex];
    }, [activeIndex, data]);

  const [clientDescription, setClientDescription] = useState<string | null>(null);

  useEffect(() => {
    if (description) {
      setClientDescription(description); // Set the description on the client side
    }
  }, [description]);

  useEffect(() => {
    if (swiper) {
      swiper.on("slideChange", (s) => {
        s.realIndex !== activeIndex && setActiveIndex(s.realIndex);
      });
    }
    return () => {
      if (swiper) {
        swiper.off("slideChange");
      }
    };
  }, [swiper, activeIndex]);

  return (
    <Stack color={"inherit"} alignSelf={"center"} {...restStackProps}>
      <AnimatePresence presenceAffectsLayout mode={"popLayout"}>
        <MotionStack
          key={`prefix-${activeIndex}`}
          color={"inherit"}
          width={1}
          initial={"initial"}
          whileInView={"animate"}
          exit={"exit"}
          viewport={{
            once: true,
            amount: "some",
          }}
          variants={{
            animate: {
              transition: {
                staggerChildren: 0.2,
                delayChildren: 0.2,
              },
            },
          }}
          {...InfoWrapperProps}
        >
          {prefix && (
            <MotionTypography
              variant={"subtitle1"}
              gutterBottom
              variants={childMotionVariants}
              {...PrefixTypographyProps}
            >
              {prefix}
            </MotionTypography>
          )}

          {title && (
            <MotionTypography
              variant={"h4"}
              fontSize={H5_1}
              gutterBottom
              variants={childMotionVariants}
              {...TitleTypographyProps}
            >
              {title}
            </MotionTypography>
          )}
          {suffix && (suffix.date || suffix.region) && (
            <MotionStack
              variants={childMotionVariants}
              direction="row"
              alignItems="center"
              columnGap={3}
            >
              {suffix.date && (
                <MotionTypography
                  display={"flex"}
                  gap={0.5}
                  alignItems={"center"}
                  variant={"body1"}
                  gutterBottom
                  {...SuffixTypographyProps}
                >
                  <CalendarToday fontSize="small" /> {suffix.date}
                </MotionTypography>
              )}
              {suffix.region && (
                <MotionTypography
                  display={"flex"}
                  gap={0.5}
                  alignItems={"center"}
                  variant={"body1"}
                  gutterBottom
                  {...SuffixTypographyProps}
                >
                  <FmdGood fontSize="small" /> {suffix.region}
                </MotionTypography>
              )}
            </MotionStack>
          )}
          {clientDescription && (
            <MotionTypography
              variant={"body1"}
              mb={3}
              variants={childMotionVariants}
              {...DescriptionTypographyProps}
              dangerouslySetInnerHTML={{ __html: clientDescription }}
            />
          )}
          {applyDate && (
            <MotionStack variants={childMotionVariants}>
              <MotionTypography fontSize={"14px !important"} mb={"6px"}>
                Apply by:
              </MotionTypography>
              <MotionTypography
                display={"flex"}
                gap={0.5}
                alignItems={"center"}
                variant={"body1"}
                gutterBottom
              >
                <CalendarToday fontSize="small" /> {applyDate}
              </MotionTypography>
            </MotionStack>
          )}
          {typeof btnText === "string" && btnLink && btnLink !== "#" ? (
            <AnimatedButton
              variant={"outlined"}
              color={"inherit"}
              variants={childMotionVariants}
              animationDelay={850}
              {...ButtonProps}
              href={
                ButtonProps?.href === "#"
                  ? btnLink
                  : ButtonProps?.href || btnLink
              }
            >
              {btnText}
            </AnimatedButton>
          ) : typeof btnText === "string" ? null : (
            btnText
          )}
        </MotionStack>
      </AnimatePresence>
      {isNavigation && swiper && (
        <SwiperNavigationButton
          swiper={swiper}
          alignSelf={{
            xs: "center",
            md: "flex-end",
          }}
          mt={{ xs: 5, lg: "auto" }}
          // sx={{
          //   position: { xs: "relative", md: "absolute" },
          //   right: 0,
          //   bottom: 0,
          // }}
          initial={{
            opacity: 0,
          }}
          whileInView={{
            opacity: 1,
            transition: {
              duration: 0.4,
              delay: 1,
            },
          }}
          viewport={{
            once: true,
            amount: "some",
          }}
          {...SwiperNavigationButtonProps}
        />
      )}
    </Stack>
  );
};
export default ArrowSlideInfo;

const childMotionVariants: MotionProps["variants"] = {
  initial: {
    opacity: 0,
    x: "20%",
  },
  animate: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.8,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2,
    },
  },
};
